// src/pages/POSProductList.js
import React, { useState, useEffect, useMemo } from "react";
// import InfiniteScroll from "react-infinite-scroll-component";
import { usePOSProductsContext } from "../contexts/POSProductContext";
import ProductCard from "../../CommonComponents/ProductCard";
import { useCart } from "../contexts/cartContext";
import SearchInput from "../../CommonComponents/SearchInput";
import BulkEditButton from "../../CommonComponents/BulkEditButton";
import EmptyState from "../../CommonComponents/EmptyState";
import Pagination from "../../CommonComponents/Pagination";
import { ShoppingCart, WifiOff } from "lucide-react";

// Skeleton loader
const ProductSkeletonGrid = ({ count }) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {Array.from({ length: count }).map((_, idx) => (
            <div
                key={idx}
                className="bg-white rounded-lg shadow-sm p-3 flex flex-col animate-pulse"
            >
                <div className="bg-gray-200 h-32 sm:h-36 md:h-40 lg:h-44 rounded-md relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 opacity-50 animate-[shimmer_1.5s_infinite]" />
                </div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mt-3 mb-1"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
            </div>
        ))}
    </div>
);

function POSProductList() {
    // const { user } = useAuth();
    // Only use addToCart from cart context.
    const { addToCart, syncCartPricesFromProducts } = useCart();
    const { products, loading, error, fetchProducts } = usePOSProductsContext();
    // Debug: log products and their stock to verify inventory source
    React.useEffect(() => {
        if (products && products.length > 0) {
            console.log("[POSProductList] Products for this user:", products.map(p => ({ id: p.id, name: p.name, stock: p.stock })));
        }
    }, [products]);
    React.useEffect(() => {
        if (products && products.length > 0) {
            syncCartPricesFromProducts(products);
        }
    }, [products, syncCartPricesFromProducts]);
    const [searchTerm, setSearchTerm] = useState("");
        // Reset to first page when search term changes
        useEffect(() => {
            setCurrentPage(1);
        }, [searchTerm]);
    const itemsPerPage = 20;
    const [currentPage, setCurrentPage] = useState(1);

    const [viewMode] = useState("grid");
    const [massEditMode, setMassEditMode] = useState(false);

    // Track quantities per product in bulk mode
    const [massEditQuantities, setMassEditQuantities] = useState({});

    // Filter products by search and sort: in-stock first
    const filteredProducts = useMemo(() => {
        return products
            .filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
            .sort((a, b) => {
                const aInStock = (a.stock || 0) > 0;
                const bInStock = (b.stock || 0) > 0;
                if (aInStock === bInStock) return a.name.localeCompare(b.name);
                return aInStock ? -1 : 1;
            });
    }, [products, searchTerm]);

    // Pagination logic
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const paginatedProducts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredProducts.slice(start, start + itemsPerPage);
    }, [filteredProducts, currentPage]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };


    // Toggle mass edit mode
    const handleToggleMassEdit = () => {
        setMassEditMode((prev) => !prev);

        if (!massEditMode) {
            // Initialize quantities for bulk edit, but exclude products with stock <= 0
            const initialQuantities = {};
            products
                .filter((p) => p.stock > 0) // only include products with stock
                .forEach((p) => (initialQuantities[p.id] = 0));
            setMassEditQuantities(initialQuantities);
        }
    };

    // Handle quantity change for mass edit mode
    const handleQuantityChange = (productId, value) => {
        setMassEditQuantities(prev => ({ ...prev, [productId]: value }));
    };

    // ...existing code...

    if (loading) return <ProductSkeletonGrid count={itemsPerPage} />;

    if (error)
        return (
            <EmptyState
                icon={WifiOff}
                title="Failed to Load Products"
                message={error.message || "Check your internet connection and try again."}
                onRetry={fetchProducts}
                retryLabel="Retry"
            />
        );

    return (
        <div className="relative px-4 pt-4">
            {/* ...existing code... */}
            {/* Fixed search and bulk edit at top of grid container */}
            <div className="sticky top-0 z-40 bg-white/95 border-b border-gray-100 flex items-center justify-between gap-3 w-full max-w-4xl mb-4 px-1 py-2" style={{backdropFilter: 'blur(2px)'}}>
                <div className="flex-1">
                    <SearchInput
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder="Search products..."
                    />
                </div>
                <div className="shrink-0">
                    <BulkEditButton
                        onClick={handleToggleMassEdit}
                        title={massEditMode ? "Done" : "Bulk Edit"}
                        isActive={massEditMode}
                    />
                </div>
            </div>

            {/* Pagination Controls - Fixed at bottom INSIDE product grid container */}
            <div className="relative">
                <div
                    className={`${viewMode === "grid" ? "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" : "flex flex-col gap-2"}`}
                >
                    {paginatedProducts.length > 0 ? (
                        paginatedProducts.map((product, idx) => (
                            <ProductCard
                                key={`${product.id}-${idx}`}
                                product={product}
                                massEditMode={massEditMode}
                                viewMode={viewMode}
                                quantity={massEditQuantities[product.id] || 0}
                                onQuantityChange={(id, val) => handleQuantityChange(id, val)}
                                showAddToCart={true}
                                onAddToCart={addToCart}
                            />
                        ))
                    ) : (
                        <EmptyState icon={ShoppingCart} message="No products found" />
                    )}
                </div>

                {/* Uniform pagination style below grid, matching POSProductsTable */}
                {totalPages > 1 && (
                    <div className="sticky bottom-0 z-20 w-full flex flex-col items-center gap-0 mt-6 bg-white/95 border-t border-gray-200 shadow p-0 pt-2">
                        <div className="w-full px-2">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                        <p className="text-sm mb-0 pb-0 leading-tight" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                            Showing {filteredProducts.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}
                            – {Math.min(currentPage * itemsPerPage, filteredProducts.length)}{' '}
                            of {filteredProducts.length} items | Page {currentPage} of {totalPages}
                        </p>
                    </div>
                )}
            </div>

            {/* ...product grid and fixed pagination now handled above... */}
        </div>
    );
}
export default POSProductList;
